import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Tabs,
  Tab,
  Avatar,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  SmartToy,
  AutoAwesome,
  PlayArrow,
  CheckCircle,
  Assessment,
  ReceiptLong,
  ArrowForward,
  Speed,
  Security,
  Psychology,
  Description
} from '@mui/icons-material';
import ScrumAILogo from '../components/common/ScrumAILogo';
import InviteNotetakerDialog from '../components/common/InviteNotetakerDialog';
import SignInDialog from '../components/common/SignInDialog';
import { useAuth } from '../context/AuthContext';
import { mockProjects, mockActions } from '../data/mockData';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, openSignInModal, targetAction, signInWithGoogle } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [featureTab, setFeatureTab] = useState(0);
  const [brdModalOpen, setBrdModalOpen] = useState(false);

  const handleLaunchWorkspace = () => {
    if (!user) {
      openSignInModal('workspace');
    } else {
      navigate('/workspace');
    }
  };

  const handleInviteNotetaker = () => {
    if (!user) {
      openSignInModal('inviteNotetaker');
    } else {
      setInviteOpen(true);
    }
  };

  const handleAuthSuccess = () => {
    if (targetAction === 'workspace') {
      navigate('/workspace');
    } else if (targetAction === 'inviteNotetaker') {
      setInviteOpen(true);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--hp-bg)', color: 'var(--hp-text)', overflowX: 'hidden' }}>
      
      {/* Top Banner Navigation */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'rgba(247, 249, 252, 0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--hp-border)',
          px: { xs: 2, md: 5 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <ScrumAILogo fontSize="1.35rem" showBadge clickable onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button
              component="a"
              href="#capabilities"
              sx={{ color: 'var(--hp-text-secondary)', textTransform: 'none', fontWeight: 500, '&:hover': { color: 'var(--hp-primary)' } }}
            >
              Capabilities
            </Button>
            <Button
              component="a"
              href="#preview"
              sx={{ color: 'var(--hp-text-secondary)', textTransform: 'none', fontWeight: 500, '&:hover': { color: 'var(--hp-primary)' } }}
            >
              Live Preview
            </Button>
            <Button
              component="a"
              href="#deep-dive"
              sx={{ color: 'var(--hp-text-secondary)', textTransform: 'none', fontWeight: 500, '&:hover': { color: 'var(--hp-primary)' } }}
            >
              Deep Dive
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleLaunchWorkspace}
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: 'var(--hp-primary)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(79, 107, 255, 0.3)',
                  '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
                }}
              >
                Workspace
              </Button>
              <Button
                variant="outlined"
                onClick={async () => {
                  const success = await signInWithGoogle();
                  if (success) {
                    navigate('/workspace');
                  }
                }}
                sx={{
                  borderColor: 'var(--hp-border)',
                  color: 'var(--hp-text)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: 'var(--hp-surface-raised)',
                  '&:hover': { bgcolor: 'var(--hp-surface)' }
                }}
              >
                Sign in
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={async () => {
                const success = await signInWithGoogle();
                if (success) {
                  navigate('/workspace');
                }
              }}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
              }
              sx={{
                bgcolor: '#FFFFFF',
                color: '#1F2937',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                px: 2.5,
                py: 0.9,
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                '&:hover': {
                  bgcolor: '#F9FAFB',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Box>

      {/* Hero Section */}
      <Box sx={{ px: { xs: 2, md: 6 }, pt: { xs: 5, md: 8 }, pb: 6, maxWidth: 1280, mx: 'auto', textAlign: 'center' }}>
        
        {/* Official App Name Badge */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', px: 2, py: 0.8, borderRadius: '24px', mb: 3 }}>
          <AutoAwesome sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: '0.2px' }}>
            ScrumAIApp — Official Next-Gen AI Project & Meeting Workspace
          </Typography>
        </Box>

        {/* Main Headline with exact app name ScrumAIApp */}
        <Typography
          component="h1"
          variant="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2.6rem', sm: '3.8rem', md: '4.6rem' },
            letterSpacing: '-1.5px',
            lineHeight: 1.1,
            mb: 1.5
          }}
        >
          <Box component="span" sx={{ color: 'var(--hp-text)' }}>Scrum</Box>
          <Box component="span" sx={{ color: 'var(--hp-primary)' }}>AI</Box>
          <Box component="span" sx={{ color: 'var(--hp-text)' }}>App</Box>
        </Typography>

        <Typography
          component="h2"
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'var(--hp-text)',
            fontSize: { xs: '1.25rem', sm: '1.6rem', md: '2rem' },
            letterSpacing: '-0.5px',
            mb: 2.5
          }}
        >
          AI-Powered Meeting Intelligence & Jira Project Management
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          sx={{
            color: 'var(--hp-text-secondary)',
            fontWeight: 400,
            maxWidth: 840,
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.2rem' },
            mb: 4
          }}
        >
          Welcome to <strong>ScrumAIApp</strong>. ScrumAIApp is an AI-powered meeting intelligence and project management platform built for agile corporate employees, engineering managers, and scrum teams. Seamlessly record meetings, ask natural-language questions grounded in transcripts, auto-generate Jira epics and stories, author BRDs, and monitor sprint health.
        </Typography>

        {/* CTA Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 6 }}>
          {!user && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => openSignInModal('workspace')}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
              }
              sx={{
                bgcolor: 'var(--hp-surface-raised)',
                color: 'var(--hp-text)',
                borderColor: 'var(--hp-border)',
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                py: 1.4,
                px: 3.5,
                boxShadow: '3px 3px 8px var(--hp-shadow-dark), -3px -3px 8px var(--hp-shadow-light)',
                '&:hover': { bgcolor: 'var(--hp-surface)' }
              }}
            >
              Sign in with Google
            </Button>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleLaunchWorkspace}
            endIcon={<ArrowForward />}
            sx={{
              bgcolor: 'var(--hp-primary)',
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.4,
              boxShadow: '6px 6px 18px rgba(79, 107, 255, 0.35)',
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Open ScrumAI Workspace
          </Button>
        </Box>

        {/* Metrics Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 2, md: 6 },
            flexWrap: 'wrap',
            pt: 2,
            pb: 2,
            borderTop: '1px solid var(--hp-border)',
            borderBottom: '1px solid var(--hp-border)'
          }}
        >
          {[
            { label: 'Scrum Health Score', val: '78 / 100', color: 'var(--hp-success)' },
            { label: 'Jira Tickets Automated', val: '100% Grounded', color: 'var(--hp-primary)' },
            { label: 'Google Authentication', val: 'Firebase Auth', color: 'var(--hp-primary)' },
            { label: 'Meeting Synthesis', val: 'Instant Audio & BRD', color: 'var(--hp-text)' }
          ].map((m, idx) => (
            <Box key={idx} sx={{ textAlign: 'center', px: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: m.color }}>
                {m.val}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontWeight: 500 }}>
                {m.label}
              </Typography>
            </Box>
          ))}
        </Box>

      </Box>

      {/* SECTION: Interactive Three-Column Workspace Preview */}
      <Box id="preview" sx={{ px: { xs: 2, md: 6 }, py: 6, maxWidth: 1320, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip label="Authentic 3-Column Desktop Architecture" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600, mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            The ScrumAIApp Desktop Workspace
          </Typography>
          <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ maxWidth: 700, mx: 'auto', mt: 1 }}>
            Engineered precisely according to specification: Projects on the left (22%), AI Assistant in center (48%), and Meeting Intelligence on the right (30%).
          </Typography>
        </Box>

        {/* Miniature Interactive Workspace Card */}
        <Box
          className="neumorphic-card"
          sx={{
            p: { xs: 1.5, md: 2.5 },
            bgcolor: 'var(--hp-bg)',
            border: '2px solid var(--hp-border)',
            borderRadius: '24px',
            boxShadow: '10px 10px 30px var(--hp-shadow-dark), -10px -10px 30px var(--hp-shadow-light)',
            overflow: 'hidden'
          }}
        >
          {/* Mock Window Top Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, mb: 2, borderBottom: '1px solid var(--hp-border)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 0.8 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FF5F56' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27C93F' }} />
              </Box>
              <ScrumAILogo fontSize="1.1rem" iconSize={26} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip size="small" label="Live Interactive Preview" color="primary" variant="outlined" />
              <Button size="small" variant="contained" onClick={handleLaunchWorkspace} sx={{ bgcolor: 'var(--hp-primary)', textTransform: 'none', borderRadius: '8px' }}>
                Full Screen
              </Button>
            </Box>
          </Box>

          {/* 3-Column Layout Mockup */}
          <Grid container spacing={2}>
            
            {/* Column 1: Projects & Health */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Box className="neumorphic-card" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                  Active Jira Projects
                </Typography>
                
                {mockProjects.slice(0, 3).map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      p: 1.2,
                      borderRadius: '10px',
                      bgcolor: p.id === '2' ? 'var(--hp-primary-light)' : 'var(--hp-surface)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      cursor: 'pointer'
                    }}
                  >
                    <Avatar sx={{ bgcolor: `${p.color}25`, color: p.color, width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700 }}>
                      {p.code}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" color="var(--hp-text-secondary)">
                        {p.epicCount} Epics • {p.storyCount} Stories
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {/* Scrum Health Card Mini */}
                <Box sx={{ mt: 'auto', p: 1.5, bgcolor: 'var(--hp-surface)', borderRadius: '14px', border: '1px solid var(--hp-border)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Scrum Health</Typography>
                    <Chip size="small" label="78 Good" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                  </Box>
                  <LinearProgress variant="determinate" value={78} sx={{ height: 6, borderRadius: 3, bgcolor: 'var(--hp-border)', '& .MuiLinearProgress-bar': { bgcolor: 'var(--hp-success)' } }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="var(--hp-text-secondary)">72% On Track</Typography>
                    <Typography variant="caption" color="var(--hp-danger)">10% Blocked</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Column 2: Center AI Assistant */}
            <Grid size={{ xs: 12, md: 5.5 }}>
              <Box className="neumorphic-card" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {/* Assistant Welcome Banner */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, bgcolor: 'var(--hp-surface)', borderRadius: '12px' }}>
                  <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }}>
                    <SmartToy />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>ScrumAI Project Copilot</Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">Grounded in your meetings, Jira tickets, and sprint status.</Typography>
                  </Box>
                </Box>

                {/* Prompt Chips */}
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                  <Chip size="small" label="Summarize latest meeting" icon={<ReceiptLong fontSize="small" />} sx={{ fontSize: '0.75rem', bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }} />
                  <Chip size="small" label="Create Jira stories" icon={<CheckCircle fontSize="small" />} sx={{ fontSize: '0.75rem' }} />
                  <Chip size="small" label="Generate BRD" icon={<Assessment fontSize="small" />} sx={{ fontSize: '0.75rem' }} />
                </Box>

                {/* Chat Bubble with Embedded Jira Actions */}
                <Box sx={{ bgcolor: 'var(--hp-surface-raised)', p: 1.5, borderRadius: '14px', border: '1px solid var(--hp-border)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Here is the synthesis of today's Product Sync:
                  </Typography>
                  
                  <Box sx={{ p: 1, bgcolor: 'var(--hp-bg)', borderRadius: '8px', borderLeft: '3px solid var(--hp-primary)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                      Automated Jira Actions Executed:
                    </Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      • Created Epic: <strong>MOB-1234</strong> (Mobile App v2.0)<br />
                      • Created 4 Stories for Biometric Auth (<strong>MOB-1235 to 1238</strong>)<br />
                      • Drafted BRD specification document
                    </Typography>
                  </Box>
                </Box>

                {/* Mini composer */}
                <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                  <Box className="neumorphic-input" sx={{ flex: 1, py: 0.5, fontSize: '0.85rem', color: 'var(--hp-text-secondary)' }}>
                    Ask anything about this meeting or sprint...
                  </Box>
                  <Button size="small" variant="contained" sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '10px' }}>
                    Send
                  </Button>
                </Box>

              </Box>
            </Grid>

            {/* Column 3: Meetings & Media Player */}
            <Grid size={{ xs: 12, md: 3.5 }}>
              <Box className="neumorphic-card" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Selected Meeting</Typography>
                  <Chip size="small" label="Oct 14" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>

                <Box sx={{ bgcolor: 'var(--hp-surface)', p: 1.5, borderRadius: '12px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Product Sync (Mobile App)</Typography>
                  <Typography variant="caption" color="var(--hp-text-secondary)">10:00 - 10:45 AM • 8 participants</Typography>
                  
                  {/* Mock Audio Bar */}
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }}>
                      <PlayArrow fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1, height: 4, bgcolor: 'var(--hp-border)', borderRadius: 2, position: 'relative' }}>
                      <Box sx={{ width: '40%', height: '100%', bgcolor: 'var(--hp-primary)', borderRadius: 2 }} />
                    </Box>
                    <Typography variant="caption" color="var(--hp-text-secondary)">15:20 / 45m</Typography>
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                  Key Meeting Decisions:
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ lineHeight: 1.4 }}>
                  1. Biometric auth approved for sprint release.<br />
                  2. Beta target set for Nov 15, 2026.<br />
                  3. Analytics dashboard prioritized for Phase 2.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setBrdModalOpen(true)}
                  startIcon={<Description fontSize="small" />}
                  sx={{ mt: 'auto', textTransform: 'none', borderRadius: '8px', borderColor: 'var(--hp-border)', color: 'var(--hp-primary)' }}
                >
                  Inspect BRD Document
                </Button>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Box>

      {/* SECTION: The 7 Core Product Capabilities */}
      <Box id="capabilities" sx={{ px: { xs: 2, md: 6 }, py: 8, maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="Full Specification Implementation" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600, mb: 1 }} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.8rem' } }}>
            What ScrumAIApp Does
          </Typography>
          <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ maxWidth: 760, mx: 'auto', mt: 1.5, fontSize: '1.1rem' }}>
            Built specifically to solve corporate agile bottlenecks. Replace messy notes and manual Jira ticket creation with autonomous meeting intelligence.
          </Typography>
        </Box>

        <Grid container spacing={3.5}>
          {[
            {
              icon: <SmartToy sx={{ fontSize: 32 }} />,
              title: '1. Invite an AI Notetaker to Meetings',
              desc: 'Seamlessly dispatch the ScrumAI bot to Zoom, Google Meet, Microsoft Teams, or Webex. It joins autonomously, captures high-fidelity audio, and transcribes real-time discussions with speaker diarization.',
              highlight: 'Zero plugins required • Any platform'
            },
            {
              icon: <ReceiptLong sx={{ fontSize: 32 }} />,
              title: '2. Recordings, Transcripts & Summaries',
              desc: 'Access past and upcoming meetings with instant audio scrubbing, synchronized verbatim transcripts, concise executive summaries, and tagged key architectural & product decisions.',
              highlight: 'Verbatim timestamped transcript'
            },
            {
              icon: <Psychology sx={{ fontSize: 32 }} />,
              title: '3. Grounded Natural-Language Q&A',
              desc: 'Ask complex contextual questions about any meeting (e.g. "What did the client say about pricing?", "What was agreed for beta launch?"). Answers are strictly grounded in meeting transcripts with citations.',
              highlight: 'Zero hallucinations • Grounded in evidence'
            },
            {
              icon: <CheckCircle sx={{ fontSize: 32 }} />,
              title: '4. Automated Jira Epics & User Stories',
              desc: 'Never manually log tickets again. ScrumAI automatically parses agreed action items, converts them into Jira epics, user stories, acceptance criteria, and generates issue keys (MOB-1234, MOB-1235).',
              highlight: 'Full backlog synchronization'
            },
            {
              icon: <Description sx={{ fontSize: 32 }} />,
              title: '5. BRD Generation with Templates',
              desc: 'Generate comprehensive Business Requirements Documents (BRDs), functional specifications, and sprint status reports from meeting discussions using corporate-approved templates.',
              highlight: 'Executive summaries • In-scope & Out-of-scope'
            },
            {
              icon: <Speed sx={{ fontSize: 32 }} />,
              title: '6. Jira Projects & Scrum Health Score',
              desc: 'Explore Jira projects with interactive Scrum Health Score donut gauges (0-100), tracking On Track, At Risk, and Blocked tickets. Identify delivery risks and velocity bottlenecks before sprints fail.',
              highlight: 'Actionable sprint diagnostics'
            },
            {
              icon: <Security sx={{ fontSize: 32 }} />,
              title: '7. AI Agent Action Audit Trail',
              desc: 'Maintain enterprise compliance with full auditability. Review every autonomous action executed by ScrumAI, categorized by status: Completed, In-Progress, Needs Review, or Failed.',
              highlight: '100% human-in-the-loop oversight'
            }
          ].map((item, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
              <Box
                className="neumorphic-card"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 3,
                  bgcolor: 'var(--hp-surface-raised)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '10px 10px 24px var(--hp-shadow-dark), -10px -10px 24px var(--hp-shadow-light)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: 'var(--hp-primary-light)',
                    color: 'var(--hp-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'inset 2px 2px 4px #FFFFFF, inset -2px -2px 4px rgba(79, 107, 255, 0.2)'
                  }}
                >
                  {item.icon}
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--hp-text)' }}>
                  {item.title}
                </Typography>

                <Typography variant="body2" sx={{ color: 'var(--hp-text-secondary)', lineHeight: 1.6, flex: 1 }}>
                  {item.desc}
                </Typography>

                <Box sx={{ pt: 1, borderTop: '1px solid var(--hp-border)' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--hp-primary)' }}>
                    ✨ {item.highlight}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* SECTION: Deep Dive Tabbed View */}
      <Box id="deep-dive" sx={{ px: { xs: 2, md: 6 }, py: 8, maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip label="Interactive Deep Dive" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600, mb: 1 }} />
          <Typography variant="h3" sx={{ fontWeight: 800 }}>
            Inspect How ScrumAI Works Under the Hood
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Tabs
            value={featureTab}
            onChange={(e, v) => setFeatureTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              bgcolor: 'var(--hp-surface)',
              p: 0.8,
              borderRadius: '16px',
              border: '1px solid var(--hp-border)',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                borderRadius: '12px',
                color: 'var(--hp-text-secondary)',
                px: 2.5
              },
              '& .Mui-selected': {
                bgcolor: 'var(--hp-surface-raised)',
                color: 'var(--hp-primary) !important',
                boxShadow: '3px 3px 8px var(--hp-shadow-dark), -3px -3px 8px var(--hp-shadow-light)'
              },
              '& .MuiTabs-indicator': { display: 'none' }
            }}
          >
            <Tab label="1. AI Notetaker & Transcripts" />
            <Tab label="2. Grounded Chat Copilot" />
            <Tab label="3. Jira Ticket Automation" />
            <Tab label="4. Scrum Health Donut Score" />
            <Tab label="5. Agent Action Audit" />
          </Tabs>
        </Box>

        {/* Tab 0: AI Notetaker */}
        {featureTab === 0 && (
          <Box className="neumorphic-card" sx={{ p: 4, borderRadius: '24px' }}>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Autonomous Meeting Recording & Diarized Transcript
                </Typography>
                <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ mb: 2, lineHeight: 1.6 }}>
                  ScrumAI joins with one-click dispatch or calendar auto-sync. During the call, our speech models perform real-time speech-to-text with 99.4% accuracy, separating speakers and generating instant summaries.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> Supports Zoom, Google Meet, Teams, and Webex
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> Speaker detection (Akhil, Sarah, Dave, etc.)
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> Playback scrubbing with 1x, 1.5x, 2x speeds
                  </Typography>
                </Box>
                <Button variant="contained" onClick={handleInviteNotetaker} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                  Test Invite Dispatcher
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3, bgcolor: 'var(--hp-bg)', borderRadius: '16px', border: '1px solid var(--hp-border)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Transcript Sample: Oct 14 Product Sync</Typography>
                    <Chip size="small" label="Live Recording" color="success" />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'var(--hp-primary)' }}>A</Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Akhil (PM) • 10:14 AM</Typography>
                        <Typography variant="body2" color="var(--hp-text-secondary)">
                          "We need the biometric FaceID authentication done before the beta cutoff on Nov 15th."
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'var(--hp-success)' }}>S</Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Sarah (Tech Lead) • 10:15 AM</Typography>
                        <Typography variant="body2" color="var(--hp-text-secondary)">
                          "Agreed. I'll create stories for iOS Keychain and Android Keystore. Let's make sure the analytics dashboard is Phase 2."
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Grounded Chat Copilot */}
        {featureTab === 1 && (
          <Box className="neumorphic-card" sx={{ p: 4, borderRadius: '24px' }}>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Natural-Language Conversation Grounded in Real Context
                </Typography>
                <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ mb: 2, lineHeight: 1.6 }}>
                  Never wonder what was discussed 3 weeks ago. The ScrumAI conversational assistant answers questions directly using the meeting transcript and Jira backlog, ensuring complete accuracy.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  <Chip label="Summarize latest meeting" variant="outlined" sx={{ bgcolor: 'var(--hp-surface)' }} />
                  <Chip label="Show project health" variant="outlined" sx={{ bgcolor: 'var(--hp-surface)' }} />
                  <Chip label="What are the main blockers?" variant="outlined" sx={{ bgcolor: 'var(--hp-surface)' }} />
                </Box>
                <Button variant="contained" onClick={handleLaunchWorkspace} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                  Try Assistant in Workspace
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3, bgcolor: 'var(--hp-bg)', borderRadius: '16px', border: '1px solid var(--hp-border)' }}>
                  <Box sx={{ p: 1.5, bgcolor: 'var(--hp-primary-light)', borderRadius: '12px', mb: 2, ml: 'auto', maxWidth: '80%' }}>
                    <Typography variant="body2" sx={{ color: 'var(--hp-text)' }}>
                      What did Sarah and Akhil agree on regarding the beta timeline?
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: 'var(--hp-surface-raised)', borderRadius: '12px', border: '1px solid var(--hp-border)', maxWidth: '90%' }}>
                    <Typography variant="caption" color="var(--hp-primary)" sx={{ fontWeight: 700 }}>
                      ScrumAI Copilot (Grounded in Product Sync):
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: 'var(--hp-text-secondary)' }}>
                      Akhil and Sarah agreed to target <strong>November 15, 2026</strong> for the beta release. Biometric auth is confirmed in-scope, while the analytics dashboard was moved to Phase 2.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 2: Jira Automation */}
        {featureTab === 2 && (
          <Box className="neumorphic-card" sx={{ p: 4, borderRadius: '24px' }}>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Autonomous Jira Epic & Story Synthesis
                </Typography>
                <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ mb: 2, lineHeight: 1.6 }}>
                  Agile teams spend up to 4 hours per sprint writing user stories. ScrumAI listens to your planning and grooming discussions and formats tickets ready for Jira with titles, descriptions, and acceptance criteria.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> Jira Epics (e.g. MOB-1234 Mobile App v2.0)
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> User Stories with acceptance criteria
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 20 }} /> Issue keys linked directly inside the chat UI
                  </Typography>
                </Box>
                <Button variant="contained" onClick={handleLaunchWorkspace} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                  View Jira Backlog
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {mockActions.map((action) => (
                    <Box key={action.id} sx={{ p: 2, bgcolor: 'var(--hp-bg)', borderRadius: '14px', border: '1px solid var(--hp-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckCircle sx={{ color: 'var(--hp-success)' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{action.description}</Typography>
                          <Typography variant="caption" color="var(--hp-text-secondary)">Status: {action.status.toUpperCase()}</Typography>
                        </Box>
                      </Box>
                      {action.jiraKey && (
                        <Chip label={action.jiraKey} color="primary" size="small" sx={{ fontWeight: 700 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 3: Scrum Health Score */}
        {featureTab === 3 && (
          <Box className="neumorphic-card" sx={{ p: 4, borderRadius: '24px' }}>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Real-time Scrum Health Score Gauge
                </Typography>
                <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ mb: 2, lineHeight: 1.6 }}>
                  Our proprietary scoring formula monitors sprint velocity, story blockers, and unassigned action items. With a 78/100 default gauge and granular On Track, At Risk, and Blocked indicators, you will always know if delivery is safe.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-success)' }}>72%</Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">On Track</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-warning)' }}>18%</Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">At Risk</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-danger)' }}>10%</Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">Blocked</Typography>
                  </Box>
                </Box>
                <Button variant="contained" onClick={() => navigate('/projects')} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                  Explore All Project Health Cards
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 4, bgcolor: 'var(--hp-bg)', borderRadius: '20px', border: '1px solid var(--hp-border)', textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Mobile App v2.0</Typography>
                  <Box sx={{ width: 140, height: 140, borderRadius: '50%', mx: 'auto', bgcolor: 'var(--hp-surface-raised)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '8px solid var(--hp-primary)', boxShadow: '6px 6px 16px var(--hp-shadow-dark), -6px -6px 16px var(--hp-shadow-light)', my: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>78</Typography>
                    <Typography variant="caption" color="var(--hp-primary)" sx={{ fontWeight: 700 }}>GOOD</Typography>
                  </Box>
                  <Typography variant="body2" color="var(--hp-text-secondary)">
                    Illustrative mock data ready for live Jira REST API webhooks.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 4: Agent Action Audit */}
        {featureTab === 4 && (
          <Box className="neumorphic-card" sx={{ p: 4, borderRadius: '24px' }}>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Enterprise Compliance & Action Audit Trail
                </Typography>
                <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ mb: 2, lineHeight: 1.6 }}>
                  AI agents must be accountable. ScrumAI logs every ticket created, BRD generated, and status update posted. Teams can inspect, approve, or undo actions before they hit production systems.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                  <Chip label="Completed: 4" color="success" size="small" />
                  <Chip label="In Progress: 1" color="primary" size="small" />
                  <Chip label="Needs Review: 0" color="warning" size="small" />
                  <Chip label="Failed: 0" color="error" size="small" />
                </Box>
                <Button variant="contained" onClick={() => navigate('/templates')} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                  View Document Templates
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3, bgcolor: 'var(--hp-bg)', borderRadius: '16px', border: '1px solid var(--hp-border)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Audit Trail Ledger</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, bgcolor: 'var(--hp-surface-raised)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Created Epic MOB-1234</Typography>
                      <Chip label="Success" size="small" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }} />
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'var(--hp-surface-raised)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Exported BRD v2.0</Typography>
                      <Chip label="Saved" size="small" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }} />
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'var(--hp-surface-raised)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Synced Jira Sprint Velocity</Typography>
                      <Chip label="Verified" size="small" sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 600 }} />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

      </Box>

      {/* SECTION: Final CTA Banner */}
      <Box sx={{ px: { xs: 2, md: 6 }, py: 10, maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
        <Box
          className="neumorphic-card"
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: '28px',
            bgcolor: 'var(--hp-surface-raised)',
            boxShadow: '12px 12px 32px var(--hp-shadow-dark), -12px -12px 32px var(--hp-shadow-light)',
            border: '1px solid var(--hp-border)'
          }}
        >
          <ScrumAILogo fontSize="2.4rem" iconSize={48} showBadge />
          
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 3, mb: 2, fontSize: { xs: '1.8rem', md: '2.6rem' } }}>
            Ready to upgrade your Scrum workflow?
          </Typography>

          <Typography variant="body1" color="var(--hp-text-secondary)" sx={{ maxWidth: 640, mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
            Experience automated meeting intelligence, instant Jira backlog tickets, and real-time sprint health calculation today.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {!user && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => openSignInModal('workspace')}
                startIcon={
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                }
                sx={{
                  bgcolor: 'var(--hp-surface-raised)',
                  color: 'var(--hp-text)',
                  borderColor: 'var(--hp-border)',
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  py: 1.5,
                  px: 4,
                  boxShadow: '3px 3px 8px var(--hp-shadow-dark), -3px -3px 8px var(--hp-shadow-light)',
                  '&:hover': { bgcolor: 'var(--hp-surface)' }
                }}
              >
                Sign In with Google
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleLaunchWorkspace}
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1.05rem',
                px: 4.5,
                py: 1.5,
                boxShadow: '6px 6px 18px rgba(79, 107, 255, 0.4)',
                '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
              }}
            >
              Launch Live Workspace
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, px: 3, borderTop: '1px solid var(--hp-border)', textAlign: 'center', bgcolor: 'var(--hp-surface)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <ScrumAILogo fontSize="1.1rem" iconSize={26} />
        </Box>
        <Typography variant="body2" color="var(--hp-text-secondary)">
          ScrumAIApp — AI-Powered Meeting Intelligence & Scrum Project Management Platform.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => navigate('/privacy')}
            sx={{
              color: 'var(--hp-primary)',
              textTransform: 'none',
              fontSize: '0.82rem',
              fontWeight: 600,
              p: 0,
              minWidth: 'auto',
              '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
            }}
          >
            Privacy Policy
          </Button>
          <Typography variant="caption" color="var(--hp-text-secondary)">•</Typography>
          <Typography variant="caption" color="var(--hp-text-secondary)">
            Support:{' '}
            <a href="mailto:joinme@scrumaiapp.com" style={{ color: 'var(--hp-primary)', textDecoration: 'none', fontWeight: 600 }}>
              joinme@scrumaiapp.com
            </a>
          </Typography>
          <Typography variant="caption" color="var(--hp-text-secondary)">•</Typography>
          <Typography variant="caption" color="var(--hp-text-secondary)">
            Website: https://scrumaiapp.com
          </Typography>
        </Box>
        <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ mt: 1, display: 'block' }}>
          Application Name: ScrumAIApp • © {new Date().getFullYear()} ScrumAIApp. All rights reserved.
        </Typography>
      </Box>

      {/* Auth Sign-In Dialog (Calendar Permission Notice) */}
      <SignInDialog onSuccess={handleAuthSuccess} />

      {/* Invite Notetaker Modal */}
      <InviteNotetakerDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

      {/* BRD Preview Modal */}
      <Dialog open={brdModalOpen} onClose={() => setBrdModalOpen(false)} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Business Requirements Document (BRD) Preview</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2, bgcolor: 'var(--hp-surface)', borderRadius: '12px', border: '1px solid var(--hp-border)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Project: Mobile App v2.0</Typography>
            <Typography variant="caption" color="var(--hp-text-secondary)">Source: Product Sync Meeting (Oct 14, 2026)</Typography>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>1. Executive Summary</Typography>
            <Typography variant="body2" color="var(--hp-text-secondary)">
              The mobile application requires biometric authentication (FaceID / Fingerprint) to enhance corporate security compliance. Target beta launch is scheduled for November 15, 2026.
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>2. Associated Jira Tickets</Typography>
            <Typography variant="body2" color="var(--hp-text-secondary)">
              • MOB-1234: [Epic] Mobile App v2.0 Core Infrastructure<br />
              • MOB-1235: Biometric Auth Keychain Storage Handler<br />
              • MOB-1236: Fallback PIN & Passcode Recovery Flow<br />
              • MOB-1237: Security Compliance Audit Report
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBrdModalOpen(false)} sx={{ color: 'var(--hp-text-secondary)', textTransform: 'none' }}>Close</Button>
          <Button variant="contained" onClick={() => { setBrdModalOpen(false); navigate('/templates'); }} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '10px', textTransform: 'none' }}>Open in Templates</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default LandingPage;
